import {
  completeChecklistShift,
  completeChecklistShiftFailure,
  completeChecklistShiftSuccess,
  fetchChecklistTaskById,
  fetchChecklistTaskByIdFailure,
  fetchChecklistShift,
  fetchChecklistShiftFailure,
  fetchChecklistShiftSuccess,
  fetchChecklistTasks,
  fetchChecklistTasksFailure,
  fetchChecklistTasksSuccess,
  returnChecklistShiftToWork,
  returnChecklistShiftToWorkFailure,
  returnChecklistShiftToWorkSuccess,
  resetChecklistPeriodDate,
  resetChecklistTasks,
  setChecklistPeriodDate,
  setChecklistSelectedTaskId,
  updateChecklistTaskLocally,
  upsertChecklistTask,
} from "./actions";
import { ChecklistTasksState, TaskItem, initialChecklistTasksState } from "./types";
import type { PayloadAction } from "@reduxjs/toolkit";
import { getDefaultPeriodDate } from "src/utils/date.utils";

export function checklistTasks(
  state: ChecklistTasksState = initialChecklistTasksState,
  action: PayloadAction<any>
): ChecklistTasksState {
  switch (action.type) {
    case fetchChecklistTasks.toString():
      return {
        ...state,
        entities: {},
        ids: [],
        shiftItems: [],
        status: "LOADING",
        shiftStatus: "LOADING",
        error: null,
        shiftError: null,
        filters: action.payload,
      };
    case fetchChecklistTasksSuccess.toString(): {
      const tasks = action.payload.tasks as TaskItem[];
      const entities = tasks.reduce<Record<number, TaskItem>>((acc, task) => {
        const key = Number(task.ConnectionID);
        if (!Number.isNaN(key)) {
          acc[key] = task;
        }
        return acc;
      }, {});

      return {
        ...state,
        entities,
        ids: Object.keys(entities).map((id) => Number(id)),
        status: "SUCCESS",
        filters: action.payload.filters,
        error: null,
      };
    }
    case fetchChecklistTasksFailure.toString():
      return {
        ...state,
        status: "FAILURE",
        error: action.payload ?? "Не удалось загрузить задачи чек-листа",
      };
    case fetchChecklistShift.toString():
      return {
        ...state,
        shiftStatus: "LOADING",
        shiftError: null,
        shiftFilters: action.payload,
      };
    case fetchChecklistShiftSuccess.toString():
      return {
        ...state,
        shiftItems: action.payload.shifts,
        shiftStatus: "SUCCESS",
        shiftFilters: action.payload.filters,
        shiftError: null,
      };
    case fetchChecklistShiftFailure.toString():
      return {
        ...state,
        shiftStatus: "FAILURE",
        shiftError: action.payload ?? "Не удалось загрузить смену",
      };
    case completeChecklistShift.toString():
      return {
        ...state,
        shiftMutationStatus: "LOADING",
        shiftError: null,
      };
    case completeChecklistShiftSuccess.toString(): {
      const completedShift = {
        ID: action.payload.ID,
        ResponsibleEmail: action.payload.payload.email,
        Date: action.payload.payload.date,
        Comment: action.payload.payload.comment?.trim() ?? "",
        Finished: true,
      };
      const shiftExists = state.shiftItems.some((shift) => shift.ID === completedShift.ID);

      return {
        ...state,
        shiftItems: shiftExists
          ? state.shiftItems.map((shift) => (shift.ID === completedShift.ID ? completedShift : shift))
          : [...state.shiftItems, completedShift],
        shiftMutationStatus: "SUCCESS",
        shiftError: null,
      };
    }
    case completeChecklistShiftFailure.toString():
      return {
        ...state,
        shiftMutationStatus: "FAILURE",
        shiftError: action.payload ?? "Не удалось завершить смену",
      };
    case returnChecklistShiftToWork.toString():
      return {
        ...state,
        shiftMutationStatus: "LOADING",
        shiftError: null,
      };
    case returnChecklistShiftToWorkSuccess.toString():
      return {
        ...state,
        shiftItems: state.shiftItems.map((shift) =>
          shift.ID === action.payload.ID ? { ...shift, Finished: false } : shift
        ),
        shiftMutationStatus: "SUCCESS",
        shiftError: null,
      };
    case returnChecklistShiftToWorkFailure.toString():
      return {
        ...state,
        shiftMutationStatus: "FAILURE",
        shiftError: action.payload ?? "Не удалось вернуть смену в работу",
      };
    case fetchChecklistTaskById.toString():
      return {
        ...state,
        selectedStatus: "LOADING",
        error: null,
      };
    case fetchChecklistTaskByIdFailure.toString():
      return {
        ...state,
        selectedStatus: "FAILURE",
        error: action.payload ?? "Не удалось загрузить задачу чек-листа",
      };
    case upsertChecklistTask.toString(): {
      const task = action.payload as TaskItem;
      const key = Number(task.ConnectionID);
      if (Number.isNaN(key)) {
        return state;
      }

      const ids = state.ids.includes(key) ? state.ids : [...state.ids, key];

      return {
        ...state,
        entities: {
          ...state.entities,
          [key]: task,
        },
        ids,
        selectedStatus: "SUCCESS",
      };
    }
    case updateChecklistTaskLocally.toString(): {
      const { connectionId, changes } = action.payload as { connectionId: number; changes: Partial<TaskItem> };
      const existing = state.entities[connectionId];

      if (!existing) {
        return state;
      }

      return {
        ...state,
        entities: {
          ...state.entities,
          [connectionId]: {
            ...existing,
            ...changes,
            Result: changes.Result ? { ...existing.Result, ...changes.Result } : existing.Result,
            Task: changes.Task ? { ...existing.Task, ...changes.Task } : existing.Task,
          },
        },
      };
    }
    case setChecklistSelectedTaskId.toString():
      return {
        ...state,
        selectedId: action.payload ?? null,
      };
    case setChecklistPeriodDate.toString():
      return {
        ...state,
        periodDate: action.payload,
      };
    case resetChecklistPeriodDate.toString():
      return {
        ...state,
        periodDate: getDefaultPeriodDate(),
      };
    case resetChecklistTasks.toString():
      return initialChecklistTasksState;
    default:
      return state;
  }
}

export const checklistTasksStatusSelector = (state: ChecklistTasksState) => state.status;
